import { createFileRoute } from '@tanstack/react-router'
import { Helmet } from 'react-helmet-async'

export const Route = createFileRoute(
    '/(onboarding)/(documents)/privacy-policy'
)({
    component: PrivacyPolicy,
})

function PrivacyPolicy() {
    return (
        <>
            <Helmet>
                <title>Privacy Policy - What The F*** Are You Wearing</title>
                <meta
                    name="description"
                    content="Privacy Policy for What The F*** Are You Wearing Platform"
                />
            </Helmet>

            <div className="glass-background min-h-dvh pt-safe">
                <div className="mx-auto max-w-5xl px-4 py-12 sm:px-6 lg:px-8">
                    {/* Header */}
                    <div className="mb-12 text-center">
                        <h1 className="text-4xl font-bold text-gray-900 sm:text-5xl">
                            Privacy Policy
                        </h1>
                        <p className="mt-4 text-xl text-gray-600">
                            Effective Date: 18 October 2025
                        </p>
                        <div className="mx-auto mt-4 h-1 w-24 rounded-full bg-gradient-to-r from-blue-500 to-purple-500" />
                    </div>

                    {/* Content */}
                    <div className="space-y-8">
                        {/* Introduction */}
                        <section className="rounded-2xl border border-gray-100 bg-white/80 p-6 shadow-sm backdrop-blur-sm sm:p-8">
                            <h2 className="mb-4 flex items-center gap-2 text-2xl font-bold text-gray-900">
                                <span className="flex h-8 w-8 items-center justify-center rounded-full bg-blue-100 text-sm font-bold text-blue-600">
                                    1
                                </span>
                                Introduction
                            </h2>
                            <div className="space-y-3 leading-relaxed text-gray-700">
                                <p>
                                    This Privacy Policy describes how{' '}
                                    <strong>Softwear Labs Pty Ltd</strong>{' '}
                                    ("Softwear Labs", "we", "our", or "us")
                                    collects, uses, discloses, and protects
                                    personal information when you use What The
                                    F*** Are You Wearing ("WTFAYW" or "the
                                    Platform").
                                </p>
                                <p className="rounded border-l-4 border-blue-500 bg-blue-50 p-4 text-sm">
                                    We comply with the{' '}
                                    <strong>Privacy Act 1988 (Cth)</strong> and
                                    the{' '}
                                    <strong>
                                        Australian Privacy Principles (APPs)
                                    </strong>
                                    .
                                </p>
                            </div>
                        </section>

                        {/* Information We Collect */}
                        <section className="rounded-2xl border border-gray-100 bg-white/80 p-6 shadow-sm backdrop-blur-sm sm:p-8">
                            <h2 className="mb-4 flex items-center gap-2 text-2xl font-bold text-gray-900">
                                <span className="flex h-8 w-8 items-center justify-center rounded-full bg-purple-100 text-sm font-bold text-purple-600">
                                    2
                                </span>
                                Information We Collect
                            </h2>
                            <p className="mb-4 text-gray-700">
                                We may collect:
                            </p>
                            <ul className="space-y-3">
                                <li className="flex gap-3">
                                    <span className="mt-1 text-purple-500">
                                        •
                                    </span>
                                    <div>
                                        <strong className="text-gray-900">
                                            Account Information:
                                        </strong>
                                        <span className="text-gray-700">
                                            {' '}
                                            name, email address, password,
                                            profile photo, and account
                                            preferences.
                                        </span>
                                    </div>
                                </li>
                                <li className="flex gap-3">
                                    <span className="mt-1 text-purple-500">
                                        •
                                    </span>
                                    <div>
                                        <strong className="text-gray-900">
                                            Content Data:
                                        </strong>
                                        <span className="text-gray-700">
                                            {' '}
                                            products or images you upload,
                                            collections you create, and AI
                                            try-on photos.
                                        </span>
                                    </div>
                                </li>
                                <li className="flex gap-3">
                                    <span className="mt-1 text-purple-500">
                                        •
                                    </span>
                                    <div>
                                        <strong className="text-gray-900">
                                            Usage Data:
                                        </strong>
                                        <span className="text-gray-700">
                                            {' '}
                                            log data, device identifiers,
                                            browser type, IP address, and
                                            interactions within the Platform.
                                        </span>
                                    </div>
                                </li>
                                <li className="flex gap-3">
                                    <span className="mt-1 text-purple-500">
                                        •
                                    </span>
                                    <div>
                                        <strong className="text-gray-900">
                                            Payment Data:
                                        </strong>
                                        <span className="text-gray-700">
                                            {' '}
                                            processed via third-party providers
                                            (e.g., Stripe); Softwear Labs does
                                            not store full card details.
                                        </span>
                                    </div>
                                </li>
                                <li className="flex gap-3">
                                    <span className="mt-1 text-purple-500">
                                        •
                                    </span>
                                    <div>
                                        <strong className="text-gray-900">
                                            Communications:
                                        </strong>
                                        <span className="text-gray-700">
                                            {' '}
                                            messages sent to our support team or
                                            through in-app contact forms.
                                        </span>
                                    </div>
                                </li>
                            </ul>
                        </section>

                        {/* How We Use Personal Information */}
                        <section className="rounded-2xl border border-gray-100 bg-white/80 p-6 shadow-sm backdrop-blur-sm sm:p-8">
                            <h2 className="mb-4 flex items-center gap-2 text-2xl font-bold text-gray-900">
                                <span className="flex h-8 w-8 items-center justify-center rounded-full bg-green-100 text-sm font-bold text-green-600">
                                    3
                                </span>
                                How We Use Personal Information
                            </h2>
                            <p className="mb-4 text-gray-700">
                                We use your information to:
                            </p>
                            <ul className="mb-4 space-y-2 text-gray-700">
                                <li className="flex gap-3">
                                    <span className="mt-1 text-green-500">
                                        ✓
                                    </span>
                                    <span>
                                        Provide and operate the Platform and its
                                        features (including the AI Try-On
                                        Feature).
                                    </span>
                                </li>
                                <li className="flex gap-3">
                                    <span className="mt-1 text-green-500">
                                        ✓
                                    </span>
                                    <span>
                                        Process payments and manage
                                        subscriptions.
                                    </span>
                                </li>
                                <li className="flex gap-3">
                                    <span className="mt-1 text-green-500">
                                        ✓
                                    </span>
                                    <span>
                                        Personalise your experience and maintain
                                        your profile archive.
                                    </span>
                                </li>
                                <li className="flex gap-3">
                                    <span className="mt-1 text-green-500">
                                        ✓
                                    </span>
                                    <span>
                                        Communicate with you about updates,
                                        security, and support.
                                    </span>
                                </li>
                                <li className="flex gap-3">
                                    <span className="mt-1 text-green-500">
                                        ✓
                                    </span>
                                    <span>
                                        Monitor and moderate user content for
                                        safety and compliance.
                                    </span>
                                </li>
                                <li className="flex gap-3">
                                    <span className="mt-1 text-green-500">
                                        ✓
                                    </span>
                                    <span>
                                        Improve the Platform, including
                                        aggregated or anonymised analytics.
                                    </span>
                                </li>
                            </ul>
                            <p className="rounded border-l-4 border-green-500 bg-green-50 p-4 text-sm font-medium text-green-900">
                                We do not sell personal information.
                            </p>
                        </section>

                        {/* AI Processing */}
                        <section className="rounded-2xl border border-indigo-100 bg-gradient-to-br from-indigo-50 to-purple-50 p-6 shadow-sm sm:p-8">
                            <h2 className="mb-4 flex items-center gap-2 text-2xl font-bold text-gray-900">
                                <span className="flex h-8 w-8 items-center justify-center rounded-full bg-indigo-100 text-sm font-bold text-indigo-600">
                                    4
                                </span>
                                AI Processing and Uploaded Images
                            </h2>
                            <ul className="space-y-3 text-gray-700">
                                <li className="flex gap-3">
                                    <span className="mt-1 text-indigo-500">
                                        →
                                    </span>
                                    <span>
                                        Processing may occur outside Australia
                                        in jurisdictions with different
                                        data-protection laws.
                                    </span>
                                </li>
                                <li className="flex gap-3">
                                    <span className="mt-1 text-indigo-500">
                                        →
                                    </span>
                                    <span>
                                        Uploaded images are stored in your
                                        profile archive and remain until you
                                        delete them.
                                    </span>
                                </li>
                                <li className="flex gap-3">
                                    <span className="mt-1 text-indigo-500">
                                        →
                                    </span>
                                    <span>
                                        We may temporarily retain generated
                                        outputs for service integrity and abuse
                                        detection.
                                    </span>
                                </li>
                                <li className="flex gap-3">
                                    <span className="mt-1 text-indigo-500">
                                        →
                                    </span>
                                    <span>
                                        You can delete any try-on result or
                                        photo through your account settings.
                                    </span>
                                </li>
                            </ul>
                        </section>

                        {/* Disclosure of Information */}
                        <section className="rounded-2xl border border-gray-100 bg-white/80 p-6 shadow-sm backdrop-blur-sm sm:p-8">
                            <h2 className="mb-4 flex items-center gap-2 text-2xl font-bold text-gray-900">
                                <span className="flex h-8 w-8 items-center justify-center rounded-full bg-orange-100 text-sm font-bold text-orange-600">
                                    5
                                </span>
                                Disclosure of Information
                            </h2>
                            <p className="mb-4 text-gray-700">
                                We may disclose information to:
                            </p>
                            <ul className="mb-4 space-y-2 text-gray-700">
                                <li className="flex gap-3">
                                    <span className="mt-1 text-orange-500">
                                        •
                                    </span>
                                    <span>
                                        Service providers who assist in
                                        delivering the Platform.
                                    </span>
                                </li>
                                <li className="flex gap-3">
                                    <span className="mt-1 text-orange-500">
                                        •
                                    </span>
                                    <span>
                                        Law-enforcement or regulatory
                                        authorities where required by law.
                                    </span>
                                </li>
                                <li className="flex gap-3">
                                    <span className="mt-1 text-orange-500">
                                        •
                                    </span>
                                    <span>
                                        Successors in the event of a merger,
                                        acquisition, or sale of assets.
                                    </span>
                                </li>
                            </ul>
                            <p className="text-sm text-gray-600 italic">
                                All third-party service providers are bound by
                                confidentiality and data-protection obligations.
                            </p>
                        </section>

                        {/* Storage and Security */}
                        <section className="rounded-2xl border border-gray-100 bg-white/80 p-6 shadow-sm backdrop-blur-sm sm:p-8">
                            <h2 className="mb-4 flex items-center gap-2 text-2xl font-bold text-gray-900">
                                <span className="flex h-8 w-8 items-center justify-center rounded-full bg-red-100 text-sm font-bold text-red-600">
                                    6
                                </span>
                                Storage and Security
                            </h2>
                            <div className="space-y-3 text-gray-700">
                                <p>
                                    We employ administrative, technical, and
                                    physical safeguards to protect personal
                                    information from loss, misuse, or
                                    unauthorised access.
                                </p>
                                <p className="rounded border-l-4 border-amber-500 bg-amber-50 p-4 text-sm text-amber-900">
                                    <strong>Important:</strong> While we strive
                                    for security, no system is completely
                                    secure; users share information at their own
                                    risk.
                                </p>
                            </div>
                        </section>

                        {/* Your Rights and Choices */}
                        <section className="rounded-2xl border border-blue-100 bg-gradient-to-br from-blue-50 to-indigo-50 p-6 shadow-sm sm:p-8">
                            <h2 className="mb-4 flex items-center gap-2 text-2xl font-bold text-gray-900">
                                <span className="flex h-8 w-8 items-center justify-center rounded-full bg-blue-100 text-sm font-bold text-blue-600">
                                    7
                                </span>
                                Your Rights and Choices
                            </h2>
                            <p className="mb-4 font-medium text-gray-700">
                                You may:
                            </p>
                            <ul className="mb-4 space-y-2 text-gray-700">
                                <li className="flex gap-3">
                                    <span className="mt-1 text-blue-500">
                                        ✓
                                    </span>
                                    <span>
                                        Access and update your personal
                                        information.
                                    </span>
                                </li>
                                <li className="flex gap-3">
                                    <span className="mt-1 text-blue-500">
                                        ✓
                                    </span>
                                    <span>
                                        Delete photos, outputs, or your entire
                                        account at any time.
                                    </span>
                                </li>
                                <li className="flex gap-3">
                                    <span className="mt-1 text-blue-500">
                                        ✓
                                    </span>
                                    <span>
                                        Opt out of non-essential communications.
                                    </span>
                                </li>
                            </ul>
                            <div className="space-y-2 text-sm">
                                <p className="text-gray-700">
                                    Requests can be made via{' '}
                                    <a
                                        href="mailto:support@wtf.style"
                                        className="font-medium text-blue-600 underline hover:text-blue-700"
                                    >
                                        support@wtf.style
                                    </a>
                                </p>
                                <p className="text-gray-600">
                                    We will respond within a reasonable period
                                    as required by law.
                                </p>
                            </div>
                        </section>

                        {/* Children and Minors */}
                        <section className="rounded-2xl border border-gray-100 bg-white/80 p-6 shadow-sm backdrop-blur-sm sm:p-8">
                            <h2 className="mb-4 flex items-center gap-2 text-2xl font-bold text-gray-900">
                                <span className="flex h-8 w-8 items-center justify-center rounded-full bg-pink-100 text-sm font-bold text-pink-600">
                                    8
                                </span>
                                Children and Minors
                            </h2>
                            <div className="space-y-3 text-gray-700">
                                <p>
                                    The Platform is intended for users aged{' '}
                                    <strong>13 years and over</strong>.
                                </p>
                                <p>
                                    Users under 18 should obtain parental or
                                    guardian consent before registering or
                                    uploading content.
                                </p>
                            </div>
                        </section>

                        {/* Data Retention */}
                        <section className="rounded-2xl border border-gray-100 bg-white/80 p-6 shadow-sm backdrop-blur-sm sm:p-8">
                            <h2 className="mb-4 flex items-center gap-2 text-2xl font-bold text-gray-900">
                                <span className="flex h-8 w-8 items-center justify-center rounded-full bg-teal-100 text-sm font-bold text-teal-600">
                                    9
                                </span>
                                Data Retention
                            </h2>
                            <div className="space-y-3 text-gray-700">
                                <p>
                                    We retain information for as long as
                                    necessary to provide the Platform, comply
                                    with legal obligations, or resolve disputes.
                                </p>
                                <p>
                                    AI uploads remain accessible in user
                                    archives until deleted by the user.
                                </p>
                            </div>
                        </section>

                        {/* International Transfers */}
                        <section className="rounded-2xl border border-gray-100 bg-white/80 p-6 shadow-sm backdrop-blur-sm sm:p-8">
                            <h2 className="mb-4 flex items-center gap-2 text-2xl font-bold text-gray-900">
                                <span className="flex h-8 w-8 items-center justify-center rounded-full bg-cyan-100 text-sm font-bold text-cyan-600">
                                    10
                                </span>
                                International Transfers
                            </h2>
                            <div className="space-y-3 text-gray-700">
                                <p>
                                    Your information may be transferred to and
                                    processed in countries other than Australia.
                                </p>
                                <p>
                                    We take reasonable steps to ensure overseas
                                    recipients protect personal information
                                    consistent with the APPs.
                                </p>
                            </div>
                        </section>

                        {/* Changes to This Privacy Policy */}
                        <section className="rounded-2xl border border-gray-100 bg-white/80 p-6 shadow-sm backdrop-blur-sm sm:p-8">
                            <h2 className="mb-4 flex items-center gap-2 text-2xl font-bold text-gray-900">
                                <span className="flex h-8 w-8 items-center justify-center rounded-full bg-violet-100 text-sm font-bold text-violet-600">
                                    11
                                </span>
                                Changes to This Privacy Policy
                            </h2>
                            <p className="text-gray-700">
                                We may update this Policy from time to time. The
                                latest version will always be posted within the
                                Platform. Continued use constitutes acceptance
                                of any updates.
                            </p>
                        </section>

                        {/* Contact Us */}
                        <section className="rounded-2xl bg-gradient-to-br from-gray-900 to-gray-800 p-6 text-white shadow-lg sm:p-8">
                            <h2 className="mb-4 flex items-center gap-2 text-2xl font-bold">
                                <span className="flex h-8 w-8 items-center justify-center rounded-full bg-white text-sm font-bold text-gray-900">
                                    12
                                </span>
                                Contact Us
                            </h2>
                            <p className="mb-4 text-gray-200">
                                For privacy enquiries or complaints, contact:
                            </p>
                            <div className="space-y-2 rounded-xl bg-white/10 p-6 backdrop-blur-sm">
                                <p className="text-lg font-bold">
                                    Softwear Labs Pty Ltd
                                </p>
                                <p className="text-gray-200">
                                    Suite 2, Level 3 7-15 Macquarie Place
                                    <br />
                                    Sydney 2000 NSW
                                </p>
                                <p className="pt-2">
                                    <span className="text-gray-300">
                                        Email:{' '}
                                    </span>
                                    <a
                                        href="mailto:support@wtf.style"
                                        className="font-medium text-blue-300 underline hover:text-blue-200"
                                    >
                                        support@wtf.style
                                    </a>
                                </p>
                            </div>
                        </section>

                        {/* Footer */}
                        <div className="border-t border-gray-200 pt-8 pb-4">
                            <p className="rounded-xl bg-gray-50 p-6 text-center text-sm text-gray-600">
                                By using What The F*** Are You Wearing, you
                                agree to this privacy policy.
                            </p>
                        </div>
                    </div>
                </div>
            </div>
        </>
    )
}
